import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useExperiences } from "../../contexts/ExperienceContext";
import { useEducation } from "../../contexts/EducationContext";
import { usePublications } from "../../contexts/PublicationContext";
import { useProfile } from "../../contexts/ProfileContext";
import { formatDateRange } from "../../firebase/experienceService";
import {
  PaperClipIcon,
  CalendarIcon,
  MapPinIcon,
  AcademicCapIcon,
  TrophyIcon,
  BookOpenIcon,
  BeakerIcon,
  LanguageIcon,
  DocumentCheckIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

const ResumePage: React.FC = () => {
  const { experiences, loading: experiencesLoading } = useExperiences();
  const { education, loading: educationLoading } = useEducation();
  const { publications, loading: publicationsLoading } = usePublications();
  const { profile, loading: profileLoading } = useProfile();

  // State for collapsible skills sections - initialize as collapsed
  const [collapsedSkills, setCollapsedSkills] = useState<{[key: string]: boolean}>({});
  
  // Use ref to track if skills have been initialized to avoid dependency issues
  const skillsInitialized = useRef(false);

  // Initialize collapsed state when profile loads
  React.useEffect(() => {
    if (profile?.skills && !skillsInitialized.current) {
      const initialCollapsedState: {[key: string]: boolean} = {};
      profile.skills.forEach((skillCategory: any) => {
        initialCollapsedState[skillCategory.id] = true;
      });
      setCollapsedSkills(initialCollapsedState);
      skillsInitialized.current = true;
    }
  }, [profile?.skills]);

  // Function to toggle skill category collapse state
  const toggleSkillCategory = (categoryId: string) => {
    setCollapsedSkills(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (
    experiencesLoading ||
    educationLoading ||
    publicationsLoading ||
    profileLoading
  ) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Extract data from profile
  const researchInterests = profile.researchInterests || [];
  const certifications = profile.certifications || [];
  const participations = profile.participations || [];
  const languages = profile.languages || [];
  const softSkills = profile.softSkills || [];

  // Sort publications by year (newest first)
  const sortedPublications = [...publications].sort((a, b) => {
    const yearA = parseInt(a.year);
    const yearB = parseInt(b.year);
    return yearB - yearA;
  });

  // Display only the most recent publications (up to 3)
  const recentPublications = sortedPublications.slice(0, 3);

  return (
    <div className="container py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="section-title">Resume</h1>

        <div className="flex justify-end mb-6">
          <button
            className="flex items-center text-primary-600 dark:text-primary-400 hover:underline"
            onClick={(e) => {
              window.print();
            }}
          >
            <DocumentCheckIcon className="h-5 w-5 mr-2" />
            <span>Print Resume</span>
          </button>
        </div>

        {/* Summary Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Professional Summary</h2>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <p className="text-gray-600 dark:text-gray-400">
              {profile.resumeSummary}
            </p>
          </div>
        </div>

        {/* Experience Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <BriefcaseIcon className="h-6 w-6 mr-2 text-primary-600" />
            Professional Experience
          </h2>
          <motion.div
            className="space-y-6"
            variants={container}
            initial="hidden"
            animate="show"
          >
            {experiences.map((exp) => (
              <motion.div
                key={exp.id}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
                variants={item}
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-2">
                  <div className="flex items-center">
                    {exp.companyLogo && (
                      <div className="w-12 h-12 flex-shrink-0 mr-3 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center overflow-hidden">
                        <img
                          src={exp.companyLogo}
                          alt={`${exp.company} logo`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    <h3 className="text-xl font-semibold">{exp.position}</h3>
                  </div>
                  <span className="text-primary-600 dark:text-primary-400 font-medium mt-1 md:mt-0">
                    {formatDateRange(exp.startDate, exp.endDate)}
                  </span>
                </div>
                <div className="flex items-center mb-3 ml-0 md:ml-15">
                  <p className="text-lg text-gray-700 dark:text-gray-300">
                    {exp.company}
                  </p>
                  {exp.location && (
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                      • {exp.location}
                    </span>
                  )}
                </div>
                <ul className="space-y-2 list-disc pl-5">
                  {exp.descriptions.map((desc, index) => (
                    <li
                      key={index}
                      className="text-gray-600 dark:text-gray-400"
                    >
                      {desc}
                    </li>
                  ))}
                </ul>
                {exp.website && (
                  <div className="mt-3">
                    <a
                      href={exp.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline text-sm flex items-center"
                    >
                      <PaperClipIcon className="h-4 w-4 mr-1" />
                      Company Website
                    </a>
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Education Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <AcademicCapIcon className="h-6 w-6 mr-2 text-primary-600" />
            Education
          </h2>
          <motion.div
            className="space-y-6"
            variants={container}
            initial="hidden"
            animate="show"
          >
            {education.length > 0 ? (
              education.map((edu) => (
                <motion.div
                  key={edu.id}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  variants={item}
                >
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-3">
                    <div className="flex items-start">
                      {edu.institutionLogo && (
                        <div className="w-16 h-16 flex-shrink-0 mr-4 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center overflow-hidden">
                          <img
                            src={edu.institutionLogo}
                            alt={`${edu.institution} logo`}
                            className="w-full h-full object-contain p-1"
                          />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-semibold mb-1">
                          {edu.degree}
                        </h3>
                        {edu.fieldOfStudy && (
                          <p className="text-gray-700 dark:text-gray-300 mb-1">
                            {edu.fieldOfStudy}
                          </p>
                        )}
                        <div className="flex items-center text-gray-600 dark:text-gray-400 mb-1">
                          <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDateRange(edu.startDate, edu.endDate)}
                          </span>
                          {edu.location && (
                            <span className="flex items-center ml-4">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {edu.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 md:mt-0 md:ml-4">
                      <p className="text-lg font-medium text-gray-800 dark:text-gray-200">
                        {edu.institution}
                      </p>
                      {edu.grade && (
                        <p className="text-primary-600 dark:text-primary-400 font-medium">
                          Grade: {edu.grade}
                        </p>
                      )}
                    </div>
                  </div>

                  {edu.descriptions && edu.descriptions.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Key Achievements:
                      </h4>
                      <ul className="space-y-1 list-disc pl-5">
                        {edu.descriptions.map((desc, index) => (
                          <li
                            key={index}
                            className="text-gray-600 dark:text-gray-400 text-sm"
                          >
                            {desc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {edu.website && (
                    <div className="mt-3">
                      <a
                        href={edu.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline text-sm flex items-center"
                      >
                        <PaperClipIcon className="h-4 w-4 mr-1" />
                        Institution Website
                      </a>
                    </div>
                  )}
                </motion.div>
              ))
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 dark:text-gray-400">
                  No education entries available yet.
                </p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Research Interests */}
        {researchInterests.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <BeakerIcon className="h-6 w-6 mr-2 text-primary-600" />
              Research Interests
            </h2>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {researchInterests.map((interest) => (
                  <div
                    key={interest.id}
                    className="border-l-4 border-primary-500 pl-4"
                  >
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                      {interest.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {interest.description}
                    </p>
                  </div>
                ))}
              </div>

              {profile.researchSummary && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold mb-3">
                    Research Summary
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {profile.researchSummary}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Publications Section */}
        {recentPublications.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <BookOpenIcon className="h-6 w-6 mr-2 text-primary-600" />
              Recent Publications
            </h2>
            <div className="space-y-4">
              {recentPublications.map((pub) => (
                <motion.div
                  key={pub.id}
                  variants={item}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
                >
                  <h3 className="text-lg font-semibold mb-2">{pub.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 italic mb-2">
                    {pub.authors.join(", ")}
                  </p>
                  <p className="text-primary-600 dark:text-primary-400 text-sm mb-2">
                    {pub.journal || pub.conference}, {pub.year}
                    {pub.location && ` • ${pub.location}`}
                  </p>

                  {pub.abstract && (
                    <div className="mb-3">
                      <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                        {pub.abstract}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mb-3">
                    {pub.keywords &&
                      pub.keywords.slice(0, 5).map((keyword, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-100 dark:bg-gray-700 text-xs px-2 py-1 rounded-full text-gray-600 dark:text-gray-400"
                        >
                          {keyword}
                        </span>
                      ))}
                  </div>

                  <div className="flex items-center gap-3">
                    {pub.link && (
                      <a
                        href={pub.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                      >
                        View Publication
                      </a>
                    )}

                    {pub.doi && (
                      <a
                        href={`https://doi.org/${pub.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                      >
                        DOI: {pub.doi}
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/publications"
                className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline"
              >
                <span className="mr-2">View all publications</span>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
            </div>
          </div>
        )}

        {/* Certifications Section */}
        {certifications.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <TrophyIcon className="h-6 w-6 mr-2 text-primary-600" />
              Certifications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certifications.map((cert) => (
                <div
                  key={cert.id}
                  className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md"
                >
                  <h3 className="font-semibold mb-2">{cert.name}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
                    <span className="font-medium">Issuer:</span> {cert.issuer}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <span className="font-medium">Date:</span> {cert.date}
                  </p>
                  {cert.url && (
                    <a
                      href={cert.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                    >
                      View Certificate
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Participations Section */}
        {participations.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <TrophyIcon className="h-6 w-6 mr-2 text-primary-600" />
              Participations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {participations.map((participation) => (
                <div
                  key={participation.id}
                  className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md"
                >
                  <h3 className="font-semibold mb-2">{participation.name}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
                    <span className="font-medium">Organizer:</span> {participation.issuer}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <span className="font-medium">Date:</span> {participation.date}
                  </p>
                  {participation.url && (
                    <a
                      href={participation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                    >
                      View Details
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Languages Section */}
        {languages.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <LanguageIcon className="h-6 w-6 mr-2 text-primary-600" />
              Languages
            </h2>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {languages.map((lang: any, index: number) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {lang.name}
                    </span>
                    <span className="text-primary-600 dark:text-primary-400 text-sm bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded">
                      {lang.proficiency}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Skills Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Technical Skills</h2>
          <div className="space-y-6">
            {profile.skills.map((skillCategory: any, categoryIndex: number) => {
              const isCollapsed = collapsedSkills[skillCategory.id] !== false;
              return (
                <div key={skillCategory.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                  {/* Category Header */}
                  <div 
                    className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 p-4 cursor-pointer hover:from-primary-700 hover:to-primary-800 dark:hover:from-primary-800 dark:hover:to-primary-900 transition-all duration-200"
                    onClick={() => toggleSkillCategory(skillCategory.id)}
                  >
                    <h3 className="text-xl font-bold text-white flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-sm">
                            {skillCategory.category.charAt(0)}
                          </span>
                        </div>
                        {skillCategory.category}
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-normal mr-2 opacity-80">
                          {isCollapsed ? 'Show' : 'Hide'}
                        </span>
                        {isCollapsed ? (
                          <ChevronDownIcon className="h-5 w-5 transition-transform duration-200" />
                        ) : (
                          <ChevronUpIcon className="h-5 w-5 transition-transform duration-200" />
                        )}
                      </div>
                    </h3>
                  </div>

                  <motion.div 
                    className="overflow-hidden"
                    initial={false}
                    animate={{ 
                      height: isCollapsed ? 0 : 'auto',
                      opacity: isCollapsed ? 0 : 1 
                    }}
                    transition={{ 
                      duration: 0.3, 
                      ease: "easeInOut",
                      opacity: { duration: 0.2 }
                    }}
                  >
                    <div className="p-6">
                  {skillCategory.subcategories ? (
                    // Display the new hierarchical structure
                    <div className="space-y-6">
                      {skillCategory.subcategories.map((subcategory: any, subIndex: number) => (
                        <div key={subIndex} className="border-l-4 border-primary-200 dark:border-primary-800 pl-4">
                          <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200 flex items-center">
                            <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                            {subcategory.name}
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {subcategory.skills.map((skill: string, skillIndex: number) => (
                              <div
                                key={skillIndex}
                                className="group relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 
                                         border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-center 
                                         hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 
                                         transition-all duration-200 cursor-default"
                              >
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
                                  {skill}
                                </span>
                                {/* Subtle accent line */}
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Backward compatibility for the old structure
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {skillCategory.items?.map((skill: string, index: number) => (
                        <div
                          key={index}
                          className="group relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 
                                   border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-center 
                                   hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 
                                   transition-all duration-200 cursor-default"
                        >
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
                            {skill}
                          </span>
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </div>
                                             ))}
                     </div>
                   )}
                   </div>
                 </motion.div>
               </div>
             );
           })}
         </div>

          {/* Soft Skills */}
          {softSkills.length > 0 && (
            <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 dark:from-emerald-700 dark:to-emerald-800 p-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">S</span>
                  </div>
                  Soft Skills
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {softSkills.map((skill: any) => (
                    <div key={skill.id} className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800">
                      <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-2 flex items-center">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                        {skill.title}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                        {skill.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResumePage;
